/**
 * Badges Gallery — Iter. 406.fr.48
 * ────────────────────────────────
 * 3 tabs (Milestones / Quests / Rare) + current-week quest banner + counts.
 * Locked badges are shown greyed-out with the description so users know what
 * to chase (except rares — they stay 🔒 mystery to preserve the surprise).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, Sparkles, Target, Lock, Loader2 } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';

const RARITY_COLORS = {
  common:    { text: 'text-slate-300',   ring: 'ring-slate-500/40',   glow: 'rgba(148,163,184,0.35)' },
  uncommon:  { text: 'text-emerald-300', ring: 'ring-emerald-500/40', glow: 'rgba(52,211,153,0.4)' },
  rare:      { text: 'text-sky-300',     ring: 'ring-sky-500/40',     glow: 'rgba(56,189,248,0.45)' },
  epic:      { text: 'text-fuchsia-300', ring: 'ring-fuchsia-500/40', glow: 'rgba(217,70,239,0.5)' },
  legendary: { text: 'text-amber-300',   ring: 'ring-amber-500/50',   glow: 'rgba(251,191,36,0.55)' },
};

export default function BadgesGallery() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');

  const [tab, setTab] = useState('milestones');
  const [mine, setMine] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Force check on load so newly-eligible badges pop up
        await api.post('/badges2/check').catch(() => {});
        const [mineRes, catRes, questRes] = await Promise.all([
          api.get('/badges2/mine'),
          api.get('/badges2/catalog'),
          api.get('/badges2/quest/current'),
        ]);
        if (cancelled) return;
        setMine(mineRes.data);
        setCatalog(catRes.data);
        setQuest(questRes.data?.quest || null);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
        </div>
      </Layout>
    );
  }

  const questBadges = (mine?.badges || []).filter(b => b.family === 'quest');

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="badges-gallery-page">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-secondary/60 flex items-center justify-center hover:bg-secondary transition-colors"
            data-testid="badges-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="font-heading text-3xl uppercase tracking-tight" data-testid="badges-title">
              {fr ? 'Galerie de Badges' : 'Badges Gallery'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {mine?.counts?.total || 0} {fr ? 'débloqués' : 'unlocked'} · {catalog?.milestones?.length || 0} + {catalog?.quests_pool_size || 0}💫 + {catalog?.rares?.length || 0}🎁
            </p>
          </div>
        </div>

        {/* Current Quest banner */}
        {quest && (
          <QuestBanner quest={quest} fr={fr} />
        )}

        {/* Tabs */}
        <div
          className="flex items-center gap-1 bg-secondary/40 rounded-xl p-1 mb-4"
          data-testid="badges-tabs"
        >
          <TabButton active={tab === 'milestones'} onClick={() => setTab('milestones')}
            icon={<Trophy className="w-4 h-4" />} label={fr ? 'Jalons' : 'Milestones'} count={mine?.counts?.milestone || 0} testId="tab-milestones" />
          <TabButton active={tab === 'quests'} onClick={() => setTab('quests')}
            icon={<Target className="w-4 h-4" />} label={fr ? 'Défis' : 'Quests'} count={mine?.counts?.quest || 0} testId="tab-quests" />
          <TabButton active={tab === 'rare'} onClick={() => setTab('rare')}
            icon={<Sparkles className="w-4 h-4" />} label={fr ? 'Rares' : 'Rare'} count={mine?.counts?.rare || 0} testId="tab-rare" />
        </div>

        {/* Grid */}
        {tab === 'milestones' && (
          <BadgeGrid items={catalog?.milestones || []} fr={fr} showLocked mode="milestone" />
        )}
        {tab === 'quests' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground italic">
              {fr
                ? '🔀 Les défis changent chaque semaine — un nouveau lundi matin. Emblèmes cumulés dans ta galerie.'
                : '🔀 Quests rotate every Monday morning. Emblems collect in your gallery.'}
            </p>
            {questBadges.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {fr ? 'Aucun défi complété pour l\'instant. Termine celui de la semaine ! 🎯' : 'No quests completed yet. Finish this week\'s! 🎯'}
              </p>
            ) : (
              <BadgeGrid items={questBadges} fr={fr} showLocked={false} mode="quest" />
            )}
          </div>
        )}
        {tab === 'rare' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground italic">
              {fr
                ? '🎁 Les badges rares se débloquent en silence. Aucune indication — que la surprise !'
                : '🎁 Rare badges unlock silently. No hints — just the surprise!'}
            </p>
            <BadgeGrid items={catalog?.rares || []} fr={fr} showLocked mode="rare" />
          </div>
        )}
      </div>
    </Layout>
  );
}

const TabButton = ({ active, onClick, icon, label, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${
      active ? 'bg-amber-500/20 text-amber-200 shadow-sm' : 'text-muted-foreground hover:text-foreground'
    }`}
  >
    {icon}
    {label}
    <span className="text-[10px] opacity-70">({count})</span>
  </button>
);

const QuestBanner = ({ quest, fr }) => {
  const q = quest.quest || {};
  const pct = quest.percentage || 0;
  const done = quest.completed;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-5 rounded-2xl bg-gradient-to-br from-fuchsia-950/40 via-card/60 to-violet-950/30 ring-1 ring-fuchsia-500/30 p-4"
      style={{ boxShadow: '0 0 22px rgba(217,70,239,0.20), inset 0 0 24px rgba(217,70,239,0.05)' }}
      data-testid="quest-banner"
    >
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl leading-none">{q.emoji || '🎯'}</span>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] uppercase tracking-widest text-fuchsia-300 font-bold">
            {fr ? 'Défi de la semaine' : 'Weekly Quest'}
          </p>
          <p className="font-heading text-base font-black text-foreground">
            {fr ? q.name_fr : q.name_en}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {fr ? q.desc_fr : q.desc_en}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className={`font-heading text-2xl font-black tabular-nums ${done ? 'text-emerald-300' : 'text-fuchsia-300'}`}>
            {quest.progress}<span className="text-sm text-muted-foreground">/{quest.target}</span>
          </p>
          {done && <span className="text-[10px] font-bold text-emerald-300 uppercase">✓ {fr ? 'Complété' : 'Done'}</span>}
        </div>
      </div>
      <div className="relative w-full h-2 rounded-full bg-secondary/50 overflow-hidden" data-testid="quest-progress-bar">
        <div
          className={`h-full ${done ? 'bg-emerald-400' : 'bg-fuchsia-400'} transition-all duration-500`}
          style={{ width: `${pct}%`, boxShadow: done ? '0 0 8px rgba(52,211,153,0.6)' : '0 0 8px rgba(217,70,239,0.6)' }}
        />
      </div>
    </motion.div>
  );
};

const BadgeGrid = ({ items, fr, showLocked, mode }) => {
  const owned = items.filter(b => b.unlocked !== false);
  const locked = showLocked ? items.filter(b => b.unlocked === false) : [];
  const isRareMode = mode === 'rare';

  return (
    <div className="space-y-4">
      {owned.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
            {fr ? `Débloqués (${owned.length})` : `Unlocked (${owned.length})`}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {owned.map(b => <BadgeCell key={b.badge_id} badge={b} fr={fr} locked={false} mysterious={false} />)}
          </div>
        </>
      )}
      {locked.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-6">
            {fr ? `À débloquer (${locked.length})` : `To unlock (${locked.length})`}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {locked.map(b => <BadgeCell key={b.badge_id} badge={b} fr={fr} locked={true} mysterious={isRareMode} />)}
          </div>
        </>
      )}
    </div>
  );
};

const BadgeCell = ({ badge, fr, locked, mysterious }) => {
  const r = RARITY_COLORS[badge.rarity || 'common'] || RARITY_COLORS.common;
  return (
    <Card
      className={`p-3 flex flex-col items-center text-center transition-all ring-1 ${r.ring} ${
        locked ? 'opacity-50 grayscale' : ''
      }`}
      style={{ boxShadow: locked ? 'none' : `0 0 14px ${r.glow}` }}
      data-testid={`badge-cell-${badge.badge_id}`}
    >
      <CardContent className="p-0 pt-1 flex flex-col items-center gap-1">
        <span className="text-4xl leading-none" aria-hidden="true">
          {locked && mysterious ? '🔒' : badge.emoji}
        </span>
        <p className={`text-[11px] font-heading font-bold uppercase tracking-wider ${r.text} min-h-[26px]`}>
          {locked && mysterious ? '???' : (fr ? badge.name_fr : badge.name_en)}
        </p>
        <p className="text-[10px] text-muted-foreground leading-tight line-clamp-2 min-h-[26px]">
          {locked && mysterious
            ? (fr ? 'Débloque en explorant l\'app' : 'Unlock by exploring the app')
            : (fr ? badge.desc_fr : badge.desc_en)}
        </p>
        {locked && !mysterious && (
          <div className="flex items-center gap-1 text-[9px] text-muted-foreground mt-0.5">
            <Lock className="w-2.5 h-2.5" /> {fr ? 'Verrouillé' : 'Locked'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
